import React, { useState, useRef } from 'react';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { CustomModal } from './CustomModel/CustomModal';
import { toast } from 'react-toastify';

export const ScreenshotButton = ({ targetId, fileName = 'screenshot', asMenuOption = false, compact = false, onClick = null, previewWidth = 0 }) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [imgSrc, setImgSrc] = useState('');
    const [crop, setCrop] = useState(null);
    const [completedCrop, setCompletedCrop] = useState(null);
    const imgRef = useRef(null);

    const handleTakeScreenshot = async () => {
        setIsLoading(true);
        setModalOpen(true);
        let iframe = null;
        try {
            let iframeUrl = window.location.href;
            iframeUrl = iframeUrl.replace('/admin/tournaments', '/tournaments');
            const iframeUrlObj = new URL(iframeUrl);
            // This button is also shown in admin mode now (Header.js), but the
            // capture must always match what a regular visitor sees, not the
            // editor UI - App.js reads this same "admin" param (admin=1) to
            // decide Tournament's is_authorized prop, so dropping it here is
            // enough to force the iframe into public view regardless of which
            // mode the real page is currently in.
            iframeUrlObj.searchParams.delete('admin');
            iframeUrlObj.searchParams.set('export', 'true');
            // Without this, the page loading inside this capture iframe reads
            // the same persisted previewWidth back out of localStorage (same
            // origin) and tries to render ITS OWN nested preview iframe
            // (TournamentPreviewFrame) instead of the real Tournament content -
            // #<targetId> then never appears (it's two iframes deep instead
            // of one), and waitForElement below times out. preview_frame=1 is
            // the same flag App.js's own previewWidth-forced-to-0 branch
            // already checks for, so this reuses that instead of a new one.
            iframeUrlObj.searchParams.set('preview_frame', '1');
            iframeUrl = iframeUrlObj.toString();

            // Header's bottom-nav zoom control (+/- next to the loupe icon)
            // renders the live page inside a same-origin iframe at
            // previewWidth so the user can preview e.g. the desktop layout
            // on a phone (see TournamentPreviewFrame.js) - capturing at the
            // device's own real width here would silently ignore that choice
            // and screenshot the layout the user isn't even looking at.
            const screenWidth = previewWidth > 0 ? previewWidth : window.innerWidth;
            const screenHeight = window.innerHeight;

            iframe = document.createElement("iframe");
            iframe.src = iframeUrl;
            iframe.style.cssText = `
                position: fixed;
                top: -9999px;
                left: -9999px;
                width: ${screenWidth}px;
                height: ${screenHeight}px;
                border: none;
                z-index: -999;
                pointer-events: none;
                visibility: hidden;
            `;
            document.body.appendChild(iframe);

            await new Promise(resolve => { iframe.onload = resolve; });

            const waitForElement = (doc, id, timeout = 10000) => {
                return new Promise((resolve, reject) => {
                    const interval = setInterval(() => {
                        const el = doc.getElementById(id);
                        if (el) {
                            clearInterval(interval);
                            resolve(el);
                        }
                    }, 200);
                    setTimeout(() => {
                        clearInterval(interval);
                        reject(new Error(`Timeout waiting for #${id} to load data`));
                    }, timeout);
                });
            };

            const targetElement = await waitForElement(iframe.contentDocument, targetId);

            // The wrapper div (targetId) exists on the very first render,
            // well before its data has loaded - waiting for just that (above)
            // used to let capture proceed while the block was still showing
            // its loading state (e.g. stage 1's draws still fetching on a
            // cold load), producing a blank/black image. PageLoader (see its
            // own comment) always portals its bar to `window.top.document`
            // when running inside a preview_frame - which this iframe always
            // is - regardless of how many frames deep that top actually is,
            // so polling there is the same signal the app itself already
            // uses to know this iframe is still loading.
            const waitForLoadingToFinish = (timeout = 10000) => {
                return new Promise((resolve) => {
                    const interval = setInterval(() => {
                        if (!window.top.document.querySelector('.top-loading-bar')) {
                            clearInterval(interval);
                            clearTimeout(timer);
                            resolve();
                        }
                    }, 150);
                    const timer = setTimeout(() => {
                        clearInterval(interval);
                        resolve();
                    }, timeout);
                });
            };
            await waitForLoadingToFinish();

            // Keep iframe size at screen dimensions to enforce mobile CSS media queries and correct 100vh calculations!
            iframe.style.width = `${screenWidth}px`;
            iframe.style.height = `${screenHeight}px`;
            await new Promise(r => setTimeout(r, 200));

            // Masthead for the captured block: clone the real tournament
            // header - identity row (thumb/name/club) AND its tab nav
            // (Участники/Жеребкування/Розклад/Рейтинг) - straight out of the
            // page currently on screen, not the capture iframe, which loads
            // with preview_frame=1 (see above) and never mounts
            // TournamentHeader at all (Tournament.js skips it for that flag,
            // same as App.js's own Header skip), so there's no on-screen
            // header inside the iframe to grab.
            // window.top (not plain document) for the same nested-preview
            // reason as the loading check above. importNode is required (not
            // a plain cloneNode append) because the node comes from a
            // different document than the iframe's.
            // Shared left/right inset for both the header clone and the
            // block's own content below it - applied on targetElement
            // itself (not the header alone) so the two line up flush with
            // each other, matching the equal side margins the real page
            // gets for free from its own ancestor .container padding, which
            // capturing just this inner block on its own otherwise loses
            // entirely (content rendered edge-to-edge with nothing to its
            // left/right).
            // Matches the real site's own insets exactly, not one flattened
            // shared value: .tournament-header-wrap (index.css:9539) is a
            // flat 20px at every width, but .page_container - the actual
            // ancestor of every tab's own content, TournamentGrids/Groups/
            // Schedule/Players included - drops to 10px below 768px
            // (index.css:1634-1638, @media max-width:768px). A previous pass
            // here forced both to the same 20px "for visual consistency",
            // which is exactly backwards for a screenshot meant to read as
            // this page, not a redesign of it - the header/content padding
            // mismatch these two values produce IS what the real mobile site
            // looks like.
            const isMobile = screenWidth <= 768;
            const HEADER_PADDING = 20;
            const CONTENT_PADDING = isMobile ? 10 : 20;
            // The header clone below always insets its own content by
            // HEADER_PADDING (20, matching .tournament-header-wrap's own
            // fixed CSS - it doesn't drop to 10px on mobile the way
            // .page_container does), which on mobile is now *wider* than
            // CONTENT_PADDING. Any width reserved for side padding has to
            // use whichever of the two is bigger - reserving only
            // CONTENT_PADDING's 10px left the header, which still needs 20,
            // squeezed narrower than its own content and clipped on the
            // right.
            const PADDING_FOR_WIDTH = Math.max(CONTENT_PADDING, HEADER_PADDING);
            targetElement.style.paddingLeft = `${CONTENT_PADDING}px`;
            targetElement.style.paddingRight = `${CONTENT_PADDING}px`;
            targetElement.style.boxSizing = 'border-box';

            let headerClone = null;
            const liveHeaderWrap = window.top.document.querySelector('.tournament-header-wrap');
            if (liveHeaderWrap) {
                headerClone = iframe.contentDocument.importNode(liveHeaderWrap, true);
                // .tournament-header-wrap.tournament-header-loaded (index.css)
                // plays a one-off 0.25s fade-in animation on mount. The real
                // header on screen finished that long ago, but importNode
                // produces a brand new node - CSS animations restart on
                // insertion regardless of what the original already did, so
                // this clone silently replayed that same fade-in from
                // opacity:0 the moment it landed in the iframe. Capturing
                // mid-fade (a real risk - the stabilize loop below can settle
                // in as little as ~250ms, right at the animation's own
                // length) is exactly what made the header render half-there/
                // missing. animation:none on the whole clone (not just the
                // identity row below) kills it outright instead of racing it.
                headerClone.style.animation = 'none';
                headerClone.style.transition = 'none';
                headerClone.style.opacity = '1';
                // .tournament-header-wrap is position:sticky on the real
                // site now - nothing scrolls during a capture, so it would
                // just render at its natural in-flow position either way,
                // but domToPng's foreignObject-based rendering has its own
                // history of sticky/fixed quirks elsewhere in this file -
                // forcing it static here is the same "don't risk it" move
                // as the animation/transition resets above.
                headerClone.style.position = 'static';
                // .tournament-header-wrap carries its own real background/
                // border directly now (it's the sticky header itself, not a
                // child of some separate .main-header ancestor anymore) -
                // read straight off the live node already captured above
                // (liveHeaderWrap), not a hardcoded var(--white), so this
                // still matches dark theme too.
                const liveHeaderStyle = window.top.getComputedStyle(liveHeaderWrap);
                headerClone.style.background = liveHeaderStyle.backgroundColor;
                headerClone.style.borderBottom = liveHeaderStyle.borderBottom;
                // On the real site this background bleeds past
                // .page_container's own side inset to reach the true
                // viewport edge (see .tournament-header-wrap's own CSS) -
                // negative margin cancels targetElement's own padding above
                // so the clone's background reaches the image's true left/
                // right edges too, then its own matching padding keeps the
                // header's actual content (logo/title/tabs) inset by the
                // same amount as the block's content below it.
                headerClone.style.marginLeft = `-${CONTENT_PADDING}px`;
                headerClone.style.marginRight = `-${CONTENT_PADDING}px`;
                headerClone.style.paddingLeft = `${HEADER_PADDING}px`;
                headerClone.style.paddingRight = `${HEADER_PADDING}px`;
                headerClone.style.boxSizing = 'border-box';
                // heroProgress crossfade (TournamentHeader.js's own inline
                // `opacity: heroProgress` on .tournament-header-top, the row
                // that actually carries back-arrow+identity+menu as one -
                // .tournament-header-identity itself has no opacity of its
                // own, just inherits invisibility from this ancestor) can
                // leave the real identity row mid-fade, or - capturing
                // without ever having scrolled, heroProgress's default 0 -
                // fully invisible, at capture time. Forced to fully visible
                // here on the row that's actually faded, not its opacity-
                // less child, so the clone doesn't silently ship with just
                // its tab nav and no crest/title above it.
                const headerTop = headerClone.querySelector('.tournament-header-top');
                if (headerTop) {
                    headerTop.style.opacity = '1';
                    headerTop.style.transition = 'none';
                }
                // Back arrow and "..." menu are interactive-only controls
                // that don't work in a static image (same reasoning as the
                // real site's own header/nav never being captured directly,
                // see the capture-just-the-block comment further below) -
                // hidden on the clone so revealing the row above doesn't
                // also surface them.
                const headerBack = headerClone.querySelector('.tournament-header-back');
                if (headerBack) headerBack.style.display = 'none';
                const headerRight = headerClone.querySelector('.tournament-header-right');
                if (headerRight) headerRight.style.display = 'none';
                // A long club name (.tournament-card-club has no
                // white-space:nowrap, unlike the title above it - it's
                // allowed to wrap to 2 lines) makes the identity row taller
                // than its usual single-line height. Nothing here is
                // supposed to clip that - .tournament-header-wrap/-top/
                // -identity/-titles are all plain auto-height flex boxes in
                // their own CSS - but forced explicitly anyway on the clone
                // and these specific wrapping layers, height:auto +
                // overflow:visible, so a taller-than-usual row can never get
                // cropped no matter what fixed-height assumption (a
                // skeleton's own explicit height, etc.) might still be
                // sitting on one of these from before the clone. Deliberately
                // NOT a blanket pass over
                // every descendant - the round avatar's own overflow:hidden
                // (.tournament-header-thumb, clips a square image into a
                // circle) and the title's own (.tournament-card-title,
                // needed for its text-overflow:ellipsis single-line
                // truncation) are load-bearing and must stay untouched.
                ['.tournament-header-wrap', '.tournament-header-top', '.tournament-header-identity', '.tournament-header-titles'].forEach(sel => {
                    const el = sel === '.tournament-header-wrap' ? headerClone : headerClone.querySelector(sel);
                    if (el) {
                        el.style.overflow = 'visible';
                        el.style.height = 'auto';
                        el.style.minHeight = '0';
                    }
                });
                targetElement.insertBefore(headerClone, targetElement.firstChild);
            }

            // The header clone's own nav row (.tournament-header-tabs) is a
            // real overflow-x:auto scroller on phones (mobile tab scrolling,
            // same class the generic pass below deliberately skips widening -
            // see its own comment). Left untouched, a static image just
            // shows whatever was scrolled into view at capture time (scroll
            // position 0) and silently drops every tab past that - on a
            // 5-tab tournament (...Розклад, Рейтинг) narrow enough that they
            // don't all fit, Рейтинг never makes it into the image at all,
            // not even clipped. Unlocked here on its own terms (not tied to
            // any unrelated wide table's width, unlike the generic pass) so
            // every tab renders, and folded into forcedChildWidth below so
            // the image is actually widened enough to fit them.
            let headerTabsRequiredWidth = 0;
            if (headerClone) {
                const headerTabs = headerClone.querySelector('.tournament-header-tabs');
                if (headerTabs) {
                    headerTabs.style.overflow = 'visible';
                    headerTabs.style.overflowX = 'visible';
                    headerTabs.style.maxWidth = 'none';
                    headerTabsRequiredWidth = headerTabs.scrollWidth;
                }
            }

            // Force horizontally scrolling containers *inside the target block*
            // to be visible and stretch their parents so backgrounds/borders
            // wrap the expanded content without spilling out. Deliberately
            // scoped to targetElement, not the whole page: doing this
            // page-wide used to also catch the app's own intentional
            // horizontal scrollers outside the target (pill tab bars,
            // dropdowns, etc.) and force max-content width all the way up
            // through the header/nav, which broke the page layout instead of
            // just widening the block we actually care about.
            const allElements = targetElement.querySelectorAll('*');
            let forcedChildWidth = 0;
            const scrollableEls = [];
            allElements.forEach(el => {
                // The header clone has its own explicit, already-correct
                // width (matching targetElement via the marginLeft/Right +
                // paddingLeft/Right math above) - it's cloned straight off
                // the real site, so its own tab nav genuinely has
                // .tournament-header-tabs' real overflow-x:auto (mobile tab
                // scrolling) and would otherwise get caught here too,
                // stripping its max-width and letting it grow past the
                // header's own bounds - unlike a bracket table, growing
                // wider here doesn't reveal more useful content, it just
                // pushes the header's right edge past the final image's own
                // width, clipping it.
                if (headerClone && headerClone.contains(el)) return;
                // Same reasoning as the header-clone skip above, for the
                // in-content tab strips that sit next to a view-mode toggle
                // (TournamentGroups' .tournament-header-tabs "Всі групи"/
                // group row, TournamentGrids' .grid-tabs-scroll place row,
                // StageTabsScroll's .stage-tabs-scroll date/group pills on
                // Schedule/Groups/Grids) - all are real overflow-x:auto
                // scrollers (mobile tab scrolling) but hold nothing but short
                // tab labels, never data worth revealing more of. Forced to
                // the same shared width as an actual wide bracket/schedule
                // table elsewhere in the block, they (and their flex:1 1 auto
                // wrapper li, widened right along with them by the
                // ancestor-climb below) blew out far past whatever sits next
                // to them (a toggle button, or - .stage-tabs-scroll's own
                // case - the rest of the row) instead of staying the compact
                // pill row the real site shows.
                if (el.classList.contains('tournament-header-tabs') || el.classList.contains('grid-tabs-scroll') || el.classList.contains('stage-tabs-scroll')) return;
                const style = iframe.contentWindow.getComputedStyle(el);
                if (style.overflowX === 'auto' || style.overflowX === 'scroll' || style.overflow === 'auto') {
                    forcedChildWidth = Math.max(forcedChildWidth, el.scrollWidth);
                    scrollableEls.push(el);
                }
            });
            // Not part of scrollableEls (the header clone's own tabs aren't
            // forced to any shared width, per the skip above) but still
            // needs a say in how wide the image ends up, same as any other
            // forcedChildWidth source - otherwise unlocking its overflow
            // above just lets it render past the target's own right edge
            // uncounted, back to the same clipping this was meant to fix.
            forcedChildWidth = Math.max(forcedChildWidth, headerTabsRequiredWidth);
            // Second pass, same shared forcedChildWidth (the widest of all of
            // them) applied to every one of them - not each element's own
            // scrollWidth. Requested so multiple side-by-side bracket blocks
            // (e.g. the "all places" grids view: main draw + 3rd/5th/9th/...
            // place brackets, each its own much smaller table) line up at one
            // common width instead of each shrinking to its own content and
            // reading as a ragged, uneven column of differently-sized cards.
            // Safe to do directly on each element (rather than needing to
            // climb to a shared ancestor, the previously-tried approach the
            // comment below warns about) because every one of these tables
            // already ends its own header/body rows with a dedicated
            // `width:'100%'` spacer column (see Bracket.js/BracketClassic.js)
            // - auto table layout hands any extra forced width to that empty
            // spacer, not to the match-card columns, so a narrower bracket
            // just gains blank space on its right instead of having its own
            // cards/connector lines stretched out of proportion.
            scrollableEls.forEach(el => {
                el.style.overflow = 'visible';
                el.style.overflowX = 'visible';
                el.style.minWidth = `${forcedChildWidth}px`;
                el.style.maxWidth = 'none';

                // Stop BEFORE targetElement itself: propagating that far
                // widens ancestors outside the block (e.g. the page
                // container), the page-wide breakage described above.
                // Also skip any CSS grid container along the way (e.g.
                // Groups' own .groups-grid, nested one level inside
                // #groups-block since it also hosts
                // TournamentExportHeader now) - forcing
                // min-width:max-content onto a grid container breaks its
                // auto-fit column/row track sizing instead of just
                // widening the one scrollable child that needed it.
                // Also stop at any ancestor with more than one element
                // child - a strong signal it's a list of independent
                // items (e.g. #brackets-block's own one-<div>-per-draw
                // children in the "All brackets" view) rather than a
                // single-purpose wrapper around just this one scrollable
                // element.
                let parent = el.parentElement;
                while (parent && parent !== targetElement && parent.children.length <= 1) {
                    const parentDisplay = iframe.contentWindow.getComputedStyle(parent).display;
                    if (parentDisplay !== 'grid' && parentDisplay !== 'inline-grid') {
                        parent.style.minWidth = 'max-content';
                        parent.style.maxWidth = 'none';
                    }
                    parent = parent.parentElement;
                }
            });

            // Multiple stacked draws in #brackets-block (main + 3rd/5th/9th/
            // ... place, or several independent pools all shown under one
            // place tab) need real separation once flattened into one tall
            // shareable image - see the extraGapStyle block below (after the
            // width-forcing pass) for the actual fix and why a plain margin
            // here doesn't produce it.

            // Only force max-content width on the target when it itself scrolls
            // horizontally (e.g. tables). Forcing it unconditionally breaks CSS
            // grid layouts (e.g. the players grid), whose auto-fill columns
            // then try to fit in a single row and blow up to absurd widths.
            // Read BEFORE flipping overflow to "visible" below - some targetIds
            // (e.g. #schedule-block, which the schedule page's own
            // .schedule-table IS, not a wrapper around one) are themselves the
            // overflow-x:auto scrollport, not just a parent of one. Checking
            // getComputedStyle *after* already setting this element's own
            // overflow to "visible" always reads back "visible" - the branch
            // below silently never ran for these targets, so the target's own
            // box never grew to fit its scrolling content and stayed at its
            // pre-capture (viewport-constrained) width. The child table then
            // rendered past that box's right edge with overflow:visible and
            // no room left for the target's own right padding - the table's
            // last column landed flush against the captured image's edge.
            const targetStyle = iframe.contentWindow.getComputedStyle(targetElement);
            const targetIsSelfScrollable = targetStyle.overflowX === 'auto' || targetStyle.overflowX === 'scroll' || targetStyle.overflow === 'auto';
            targetElement.style.overflow = "visible";
            targetElement.style.height = "auto";
            targetElement.style.minHeight = "0";
            if (targetIsSelfScrollable) {
                targetElement.style.minWidth = "max-content";
            }

            // Multiple stacked bracket blocks (#brackets-block's own one-
            // <div>-per-draw children in the "All places" view) rely on
            // .bracket-table/.bracket-classic-table's own 10px margin-bottom
            // for separation - reads fine scrolling through them live, but
            // in a single tall stitched export image (main + 3rd/5th/9th/...
            // place brackets can add up to several thousand px) that same
            // 10px reads as essentially zero once the whole image is viewed
            // shrunk to fit a phone screen, making consecutive blocks look
            // like they're touching ("впритик"). Widened only for the
            // capture, not the live page - nothing wrong with the on-screen
            // spacing was reported, just how thin it reads once flattened
            // into one long shareable image.
            // A real stylesheet rule, not each element's own inline style -
            // this is a live, still-rendering React app inside the iframe
            // (TournamentGrids' own gridsReady timers/effects are still
            // settling around here), and any of its re-renders would wipe a
            // plain el.style.marginBottom write the moment React
            // reconciles that element again, since marginBottom isn't part
            // of its own JSX style prop to begin with (confirmed - this
            // silently got lost before the actual capture when tried that
            // way first). A <style> tag isn't part of React's tree, so it
            // survives every re-render between here and the capture call
            // below. Inserted before the scrollWidth/scrollHeight
            // stabilization loop (not just before domToPng) so
            // expandedHeight already accounts for the extra space.
            // Raising this value alone (tried 40px, then 80px) only grew the
            // space AFTER the very last bracket, not between consecutive
            // ones - the real cause: .bracket-table/.bracket-classic-table
            // is never the direct child of #brackets-block's own per-draw
            // wrapper - both Bracket.js and BracketClassic.js return their
            // OWN plain `<div style={{position:"relative"}}>` around it
            // first (TournamentGrids.js then wraps THAT in its own bare
            // `<div key={draw.id}>`, no class/style of its own). Neither of
            // those two wrapper divs has a border/padding/overflow of its
            // own, so a bottom margin on the innermost .bracket-table/
            // .bracket-classic-table keeps collapsing straight through both
            // of them and out past the *whole* #brackets-block stack - by
            // the time it reaches a box that actually stops collapsing
            // (#brackets-block's own parent), every draw's margin has
            // already merged into one, landing as a single block of space
            // after the last draw instead of one gap per draw.
            // `display: flow-root` on the per-draw wrapper (the direct
            // grandchild of #brackets-block, one level above the
            // `position:relative` div) gives each draw its own margin-
            // collapsing boundary - unlike `overflow: hidden`, flow-root
            // doesn't clip anything, so a bracket table still wider than
            // its wrapper (the minWidth/maxWidth rewrite above) still
            // renders in full instead of getting cut off.
            const extraGapStyle = iframe.contentDocument.createElement('style');
            extraGapStyle.textContent = `
                .bracket-table, .bracket-classic-table { margin-bottom: 5px !important; }
                #brackets-block > div > div { display: flow-root; }
            `;
            iframe.contentDocument.head.appendChild(extraGapStyle);

            // The shared-width pass above can make a descendant (e.g.
            // #brackets-block, once every bracket table in it shares the
            // widest one's width) wider than targetElement's OWN current
            // width. The header clone and any stage-tabs/place-tabs rows
            // above it are ordinary block children sized to targetElement's
            // width, not to this one descendant's new forced width - left
            // alone, they stay at the old (narrower) width while the grids
            // poke out wider underneath, so the header and the content no
            // longer share the same right edge. minWidth only ever grows
            // targetElement (never shrinks it below whatever its own content
            // already needed), and every block child - header clone
            // included, via its own marginLeft/Right cancel-and-repad trick
            // above - naturally stretches to match.
            if (forcedChildWidth > 0) {
                targetElement.style.minWidth = `${forcedChildWidth + PADDING_FOR_WIDTH * 2}px`;
            }

            // Capture just the target block, not the whole page - the app's
            // real header/nav/menu were never designed to be screenshotted
            // (fixed positioning, interactive-only controls) and came out
            // visibly broken (menu floating centered, squashed header) once
            // this captured the full body. The header clone prepended above
            // keeps the file matching what's actually on screen (tournament
            // identity + tab nav) without needing the rest of the real page
            // chrome (back arrow's target/the "..." menu's popup obviously
            // don't work in a static image, but they're harmless to include).
            await new Promise(r => setTimeout(r, 100));

            let prevWidth = -1;
            let prevHeight = -1;
            for (let i = 0; i < 6; i++) {
                const w = targetElement.scrollWidth;
                const h = targetElement.scrollHeight;
                if (w === prevWidth && h === prevHeight) break;
                prevWidth = w;
                prevHeight = h;
                await new Promise(r => setTimeout(r, 150));
            }

            // Every card (.bracket-table/.bracket-classic-table/.table-wrapper/
            // .schedule-table, index.css's shared "floating card" rule) casts
            // var(--card-shadow) downward - fine between two stacked cards
            // (it reads as normal depth, the next card sits right where it
            // fades into), but the very last one in the image has nothing
            // below it to blend into: the shadow just gets sliced off flat by
            // the image's own bottom edge, reading as a stray gray smear
            // rather than a soft fade. Only the card(s) actually touching the
            // bottommost edge (by rect.bottom, not DOM order - a bottom ROW
            // of side-by-side cards, e.g. Groups' own grid, can have more
            // than one) lose their shadow; every other card keeps it.
            const shadowedCards = Array.from(targetElement.querySelectorAll('*')).filter(el => {
                const boxShadow = iframe.contentWindow.getComputedStyle(el).boxShadow;
                return boxShadow && boxShadow !== 'none';
            });
            if (shadowedCards.length > 0) {
                const bottoms = shadowedCards.map(el => el.getBoundingClientRect().bottom);
                const maxBottom = Math.max(...bottoms);
                shadowedCards.forEach((el, i) => {
                    if (maxBottom - bottoms[i] < 2) {
                        el.style.boxShadow = 'none';
                    }
                });
            }

            // Floored to screenWidth - the header clone is meant to bleed
            // edge-to-edge (see its own marginLeft/Right cancel-and-repad
            // trick above), which only actually reaches a real edge when the
            // canvas itself is at least screenWidth wide. A narrower-content
            // tab (e.g. Information, no wide table to drive forcedChildWidth)
            // previously left the canvas sized to just the content's own
            // natural width, cropping the header short of the real screen
            // edge on its right. The bottom stops at the content's own edge
            // plus a small breathing-room margin, matching the top (flush
            // against the header clone above).
            //
            // forcedChildWidth (a scrollable descendant's own scrollWidth,
            // e.g. a wide table) never included targetElement's own
            // CONTENT_PADDING to begin with - it's measured off the child, not
            // targetElement. And once targetElement.style.overflow was set
            // to "visible" above, its scrollWidth stops accounting for that
            // same overflowing child at all (scrollWidth only grows with
            // overflow when the element actually scrolls/clips - "visible"
            // means nothing overflows *it*, by definition). So whenever
            // forcedChildWidth is what actually drives the output width,
            // its own missing padding has to be added back explicitly - left
            // out, the right padding silently vanished (the image was sized
            // to exactly fit the wide child, edge to edge, no matter what
            // padding targetElement itself was asking for).
            const BOTTOM_MARGIN = CONTENT_PADDING;
            const expandedWidth = Math.max(screenWidth, targetElement.scrollWidth, forcedChildWidth + PADDING_FOR_WIDTH * 2);
            const expandedHeight = targetElement.scrollHeight + BOTTOM_MARGIN;

            const computedStyle = iframe.contentWindow.getComputedStyle(iframe.contentDocument.body);
            let bgColor = computedStyle.getPropertyValue('--body-background').trim();
            if (!bgColor) bgColor = computedStyle.backgroundColor;
            if (bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent') bgColor = '#ffffff';

            const images = Array.from(targetElement.getElementsByTagName('img'));

            // Wait for visible images to load (max 5s inside iframe) so
            // layout has its final size before domToPng measures/renders it.
            // Fetching/embedding the images as data URIs (what html2canvas
            // needed a hand-rolled loop for, to dodge canvas tainting on
            // cross-origin logos) is handled internally by domToPng below.
            const imagePromises = images.map(img => {
                if (img.complete) return Promise.resolve();
                return new Promise(resolve => {
                    img.onload = resolve;
                    img.onerror = resolve;
                });
            });
            await Promise.race([
                Promise.all(imagePromises),
                new Promise(r => setTimeout(r, 5000))
            ]);
            images.forEach(img => {
                img.style.opacity = '1';
                img.style.transition = 'none';
            });

            // Calculate safe scale to prevent browser canvas memory limits (causing blank images)
            const maxCanvasArea = 16777216; // 16.7 MP Safari safe limit
            const currentArea = expandedWidth * expandedHeight;
            let dynamicScale = isMobile ? 1.5 : 2;

            if (currentArea * dynamicScale * dynamicScale > maxCanvasArea) {
                dynamicScale = Math.sqrt(maxCanvasArea / currentArea) * 0.95; // 0.95 for safety margin
            }
            if (dynamicScale < 0.5) dynamicScale = 0.5;

            // domToPng renders the DOM through an inlined SVG foreignObject
            // (the browser's own layout engine draws it) instead of
            // html2canvas's approach of re-implementing CSS layout by hand -
            // that's what was behind the recurring flexbox/grid/object-fit/
            // CORS-image bugs this button kept needing new workarounds for.
            // Dynamic import: modern-screenshot alone is ~800KB of this
            // bundle's JS (verified via source-map-explorer) for a feature
            // most visits never touch - loaded on demand, right when the
            // user actually presses this button, instead of shipping it to
            // every page load.
            const { domToPng } = await import('modern-screenshot');
            const dataUrl = await domToPng(targetElement, {
                scale: dynamicScale,
                backgroundColor: bgColor,
                width: expandedWidth,
                height: expandedHeight,
                // Diagnostic only (kept in prod) - participant/player avatar
                // photos were reported missing from the exported image with
                // no reproducible cause found by static review (same-origin
                // media host, no CORS/CSP headers blocking it, confirmed via
                // the live API). domToPng silently swaps a failed image's
                // fetch for an empty placeholder by default - logging here
                // surfaces the actual failed URL/reason in the console next
                // time this reproduces, instead of just a blank circle.
                fetch: {
                    placeholderImage: (cloned) => {
                        console.warn('[Screenshot] image failed to embed:', cloned?.src || cloned);
                        return '';
                    },
                },
            });
            setImgSrc(dataUrl);
            
            // Reset crop
            setCrop({ unit: 'px', width: 100, height: 100, x: 0, y: 0 });
            setCompletedCrop({ unit: 'px', width: 100, height: 100, x: 0, y: 0 });

        } catch (error) {
            console.error(error);
            toast.error(t("Failed to take screenshot"));
        } finally {
            if (iframe && document.body.contains(iframe)) {
                document.body.removeChild(iframe);
            }
            setIsLoading(false);
        }
    };

    const getCroppedBlob = () => {
        return new Promise((resolve, reject) => {
            if (!completedCrop || !imgRef.current) {
                reject(new Error('No crop'));
                return;
            }

            const image = imgRef.current;
            const canvas = document.createElement('canvas');
            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;

            const cropX = completedCrop.x * scaleX;
            const cropY = completedCrop.y * scaleY;
            const cropWidth = completedCrop.width * scaleX;
            const cropHeight = completedCrop.height * scaleY;

            canvas.width = Math.floor(cropWidth);
            canvas.height = Math.floor(cropHeight);

            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingQuality = 'high';

            ctx.drawImage(
                image,
                cropX,
                cropY,
                cropWidth,
                cropHeight,
                0,
                0,
                cropWidth,
                cropHeight
            );

            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('Failed to create blob'));
                    return;
                }
                resolve(blob);
            }, 'image/png', 1.0);
        });
    };

    const handleDownload = async () => {
        try {
            const blob = await getCroppedBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${fileName}_${new Date().getTime()}.png`;
            link.href = url;
            link.click();

            // Clean up
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            toast.success(t("Screenshot downloaded successfully"));
            setModalOpen(false);
        } catch (e) {
            console.error(e);
            toast.error(t("Failed to crop image"));
        }
    };

    const handleShare = async () => {
        try {
            const blob = await getCroppedBlob();
            const file = new File([blob], `${fileName}_${new Date().getTime()}.png`, { type: 'image/png' });

            if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file] });
                setModalOpen(false);
                return;
            }

            if (navigator.clipboard && window.ClipboardItem) {
                await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
                toast.success(t("Screenshot copied to clipboard"));
                setModalOpen(false);
                return;
            }

            toast.warn(t("Not supported in this browser"));
        } catch (e) {
            if (e.name === 'AbortError') return;
            console.error(e);
            toast.error(t("Share failed"));
        }
    };

    return (
        <>
            {compact ? (
                // Bare icon button, no label - for a tight icon row (e.g.
                // TournamentHeader.js's own .tournament-header-right, next to
                // Share/Export) rather than either of the two list-item
                // contexts below. Same action-button/matches-btn classes as
                // the Rating icon button already living in that same row
                // (TournamentRatingTab.js), for a consistent look.
                <a
                    href="#"
                    className="action-button matches-btn"
                    onClick={(e) => {
                        e.preventDefault();
                        if (onClick) onClick();
                        handleTakeScreenshot();
                    }}
                    title={t("Screenshot")}
                >
                    {isLoading ? (
                        <FeatherIcon icon="loader" className="spin" size={16} />
                    ) : (
                        <FeatherIcon icon="camera" size={16} />
                    )}
                </a>
            ) : asMenuOption ? (
                <a
                    href="#"
                    className="option pointer"
                    onClick={(e) => {
                        e.preventDefault();
                        if (onClick) onClick();
                        handleTakeScreenshot();
                    }}
                    style={{ border: 0, display: "block" }}
                >
                    {isLoading ? (
                        <><FeatherIcon icon="loader" className="spin" /> {t("Loading...")}</>
                    ) : (
                        <><FeatherIcon icon="camera" /> {t("Screenshot")}</>
                    )}
                </a>
            ) : (
                // Renders as a <li> so the caller can drop it straight into
                // .tournament-bottom-nav's <ul> alongside Share/Export -
                // that nav's own CSS (.tournament-bottom-nav li button)
                // already gives it the same icon-over-label layout as its
                // siblings.
                <li>
                    <button
                        type="button"
                        className="screenshot-nav-btn"
                        onClick={handleTakeScreenshot}
                        disabled={isLoading}
                        title={t("Screenshot")}
                    >
                        {isLoading ? (
                            <FeatherIcon icon="loader" className="spin" size={20} />
                        ) : (
                            <FeatherIcon icon="camera" size={20} />
                        )}
                        <span>{t("Screenshot")}</span>
                    </button>
                </li>
            )}

            {modalOpen && (
                <CustomModal isOpen={modalOpen} onClose={() => setModalOpen(false)} maxWidth="min(95vw, 900px)">
                    <div className="modal-form">
                        <h2>
                            <FeatherIcon icon="crop" style={{ marginRight: '10px' }} />
                            {t("Crop Screenshot")}
                        </h2>

                        <p style={{ color: 'var(--secondary-color)', fontSize: '14px', marginTop: '10px', marginBottom: '5px' }}>
                            {t("Select the area you want to save by dragging the borders of the image.")}
                        </p>

                        {isLoading ? (
                            <div className="screenshot-crop-container" style={{ marginTop: '15px', minHeight: 'min(40vh, 300px)', textAlign: 'center' }}>
                                <div>
                                    <FeatherIcon icon="loader" className="spin" size={40} style={{ color: 'var(--primary-color)' }} />
                                    <p style={{ marginTop: '15px', color: 'var(--secondary-color)' }}>{t("Generating screenshot, please wait...")}</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="screenshot-crop-container" style={{ marginTop: '15px' }}>
                                    <ReactCrop
                                        crop={crop}
                                        onChange={(pixelCrop) => setCrop(pixelCrop)}
                                        onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
                                        style={{ maxWidth: '100%', maxHeight: '100%' }}
                                    >
                                        <img
                                            ref={imgRef}
                                            src={imgSrc}
                                            alt="Screenshot preview"
                                            style={{ maxWidth: '100%', maxHeight: 'var(--screenshot-crop-max-height)', width: 'auto', height: 'auto', display: 'block' }}
                                            onLoad={(e) => {
                                                const { width, height } = e.currentTarget;
                                                // Initialize crop to the full image so selection matches the content by default
                                                const initialCrop = { unit: 'px', width, height, x: 0, y: 0 };
                                                setCrop(initialCrop);
                                                setCompletedCrop(initialCrop);
                                            }}
                                        />
                                    </ReactCrop>
                                </div>

                                <ul className="tabs" style={{ marginTop: '20px' }}>
                                    <li>
                                        <a
                                            href="#"
                                            className={`action-button btn-green ${(!completedCrop?.width || !completedCrop?.height) ? "disabled" : ""}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (!completedCrop?.width || !completedCrop?.height) return;
                                                handleShare();
                                            }}
                                        >
                                            <FeatherIcon icon="share-2" /> {t("Share")}
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="#"
                                            className={`action-button btn-cancel ${(!completedCrop?.width || !completedCrop?.height) ? "disabled" : ""}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (!completedCrop?.width || !completedCrop?.height) return;
                                                handleDownload();
                                            }}
                                        >
                                            <FeatherIcon icon="download" /> {t("Download")}
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            href="#"
                                            className="action-button btn-cancel"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setModalOpen(false);
                                            }}
                                        >
                                            <FeatherIcon icon="x" /> {t("Cancel")}
                                        </a>
                                    </li>
                                </ul>
                            </>
                        )}
                    </div>
                </CustomModal>
            )}
        </>
    );
};
